import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase, type User } from "@/lib/supabase";

interface AuthContextType {
  user: User | null;
  session: unknown | null;
  isLoading: boolean;
  isAdmin: boolean;
  authError: string | null;
  signUp: (email: string, password: string, fullName: string, isAdmin?: boolean) => Promise<{ error: Error | null; data?: unknown }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; data?: unknown }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const fetchUser = useCallback(async (userId: string, retries = 5): Promise<User | null> => {
    for (let i = 0; i < retries; i++) {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", userId)
          .maybeSingle();
        if (error) {
          console.error("Error fetching profile:", error);
          if (i < retries - 1) await new Promise((r) => setTimeout(r, 600));
          continue;
        }
        if (data) return data as User;
        // Profile not created yet (trigger delay) — wait and retry
        if (i < retries - 1) await new Promise((r) => setTimeout(r, 600));
      } catch (err) {
        console.error("Exception fetching profile:", err);
        if (i < retries - 1) await new Promise((r) => setTimeout(r, 600));
      }
    }
    return null;
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error("getSession error:", sessionError);
          setAuthError("Session error: " + sessionError.message);
        }
        if (!mounted) return;

        setSession(initialSession);
        if (initialSession?.user) {
          const profile = await fetchUser(initialSession.user.id);
          if (mounted) {
            if (profile) {
              setUser(profile);
              setAuthError(null);
            } else {
              setAuthError("Profile not found. Please contact support.");
            }
          }
        }
      } catch (err) {
        console.error("Auth init error:", err);
        if (mounted) setAuthError("Auth initialization failed.");
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.log("Auth state change:", event, newSession?.user?.id);
      setSession(newSession);
      if (newSession?.user) {
        const profile = await fetchUser(newSession.user.id);
        if (mounted) {
          if (profile) {
            setUser(profile);
            setAuthError(null);
          } else {
            // Don't clear user if we already have one — prevents redirect loops
            setUser((prev) => prev);
            setAuthError("Profile sync issue. Refreshing...");
          }
        }
      } else {
        if (mounted) {
          setUser(null);
          setAuthError(null);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUser]);

  const signUp = async (email: string, password: string, fullName: string, isAdmin = false) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role: isAdmin ? "admin" : "user",
        },
      },
    });
    return { error, data };
  };

  const signIn = async (email: string, password: string) => {
    setAuthError(null);
    // Race against a 20s timeout to prevent the iframe-hang bug where
    // signInWithPassword never resolves in the Replit preview proxy.
    const result = await Promise.race([
      supabase.auth.signInWithPassword({ email, password }),
      new Promise<{ data: null; error: Error }>((resolve) =>
        setTimeout(
          () => resolve({ data: null, error: new Error("Login timed out. Please refresh and try again.") }),
          20000,
        ),
      ),
    ]);
    const { data, error } = result as { data: { session?: { user: { id: string } } } | null; error: Error | null };
    if (error) {
      console.error("Sign in error:", error);
      return { error, data };
    }
    if (data?.session) {
      setSession(data.session);
      const profile = await fetchUser(data.session.user.id);
      if (profile) {
        setUser(profile);
        setAuthError(null);
      } else {
        setAuthError("Signed in but profile not loaded. Retrying...");
        // Retry profile fetch
        setTimeout(async () => {
          const retryProfile = await fetchUser(data.session.user.id);
          if (retryProfile) {
            setUser(retryProfile);
            setAuthError(null);
          }
        }, 1000);
      }
    }
    return { error, data };
  };

  const signOut = async () => {
    // Race against a 3s timeout — supabase auth occasionally hangs in the
    // Replit preview iframe. Local state is cleared regardless.
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } catch {
      /* ignore */
    }
    setUser(null);
    setSession(null);
    setAuthError(null);
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith("sb-") || k.includes("supabase.auth")) {
          localStorage.removeItem(k);
        }
      });
    } catch {
      /* ignore */
    }
    // Force navigation if the caller didn't redirect.
    setTimeout(() => {
      if (window.location.pathname.startsWith("/dashboard") || window.location.pathname.startsWith("/admin")) {
        window.location.href = "/login";
      }
    }, 200);
  };

  const refreshUser = async () => {
    if (session && (session as { user?: { id: string } }).user) {
      const profile = await fetchUser((session as { user: { id: string } }).user.id);
      setUser(profile);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        isAdmin: user?.role === "admin",
        authError,
        signUp,
        signIn,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}